// MAGAZINE — the /upcoming "Release schedule" timeline. A magScreenHead masthead
// over month-divider headings (Anton, via magSectionHeader), with each upcoming
// item rendered as a glass row: cover thumb + Anton title + a release-date
// sticker. Data/sort/grouping and navigation are unchanged from the row-list
// original — only the presentation moved to the magazine kit (inline styles).
import { el } from "../components/dom.js";
import { listAllMedia } from "../db/media.js";
import { buildSuggestions } from "../db/recommendations.js";
import { buildAiring, dedupeById } from "../db/airing.js";
import { listSubscriptions } from "../db/comic-subscriptions.js";
import { scheduleRows } from "../utils/comic-subscriptions.js";
import { formatReleaseDate, formatReleaseWhen } from "../utils/dates.js";
import { upcoming } from "../utils/release.js";
import { navigate, navigateBack } from "../main.js";
import {
	magScreenHead,
	magSectionHeader,
	magGlass,
	magCover,
	magSticker,
	MAG,
	MAG_ACCENTS,
} from "../components/magazine/kit.js";

// A long-form month label ("June 2026") for the divider headings; falls back to
// the year alone when an item only has a release_year.
const MONTH_FMT = new Intl.DateTimeFormat("en", { month: "long", year: "numeric" });
function monthBucket(item) {
	if (item.release_date) {
		const d = new Date(item.release_date);
		if (!Number.isNaN(d.getTime())) {
			return { key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`, label: MONTH_FMT.format(d) };
		}
	}
	const y = item.release_year != null ? String(item.release_year) : "Date TBA";
	return { key: y, label: y };
}

export async function renderReleaseSchedule(root) {
	const head = magScreenHead("Release schedule", {
		eyebrow: "Coming soon",
		onBack: () => navigateBack("/"),
	});

	const view = el("div.view", {}, head);
	root.append(view);

	// Preserve the original data logic: upcoming() already filters + sorts. In
	// addition to dated games/books, fold in not-yet-released seasons of series
	// you've watched and enjoyed (shaped like normalized media by buildSuggestions).
	const [allMedia, { upcomingSeasons }, airing, subscriptions] = await Promise.all([
		listAllMedia(),
		buildSuggestions(),
		buildAiring(),
		listSubscriptions(),
	]);
	// Dated games/books, upcoming seasons (recommendation engine + series you're
	// Watching) and the next episode of each airing series, deduped by id.
	const items = upcoming(
		dedupeById([
			...allMedia,
			...upcomingSeasons,
			...airing.upcomingSeasons,
			...airing.upcomingEpisodes,
			// Upcoming issues of subscribed comic series (stored by the daily check).
			...scheduleRows(subscriptions),
		]),
	);

	if (items.length === 0) {
		view.append(
			el(
				"div",
				{ style: { fontFamily: "'Archivo', sans-serif", fontWeight: "600", fontSize: "13px", color: MAG.dim, padding: "24px 4px", lineHeight: "1.5" } },
				"Nothing upcoming. Games and books with a future release date, upcoming seasons, the next episodes of series you're watching, and new issues of comic series you subscribe to show up here.",
			),
		);
		return;
	}

	// Group consecutive items into month sections (input is already date-sorted,
	// so a simple sequential bucket preserves the existing ordering).
	let lastKey = null;
	let monthIndex = -1;
	for (const item of items) {
		const bucket = monthBucket(item);
		if (bucket.key !== lastKey) {
			monthIndex += 1;
			lastKey = bucket.key;
			view.append(magSectionHeader(bucket.label, MAG_ACCENTS[monthIndex % MAG_ACCENTS.length]));
		}

		const when = item.release_date
			? formatReleaseWhen(item.release_date, item.release_precision)
			: String(item.release_year);
		const accent = MAG_ACCENTS[monthIndex % MAG_ACCENTS.length];

		const thumb = magCover(item, { height: "62px", width: "46px", radius: "8px" });

		const body = el(
			"div",
			{ style: { flex: "1", minWidth: "0" } },
			magSticker(when, { bg: accent, size: "9px" }),
			el("div", {
				style: {
					fontFamily: "'Anton', sans-serif", fontSize: "18px", lineHeight: ".95",
					textTransform: "uppercase", color: "#fff", marginTop: "6px",
					overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
				},
			}, item.title ?? ""),
			// Season / episode rows say which season or episode this is.
			item.reason && item.secondary
				? el("div", {
					style: {
						fontFamily: "'Archivo', sans-serif", fontWeight: "700", fontSize: "11px",
						color: MAG.dim, marginTop: "4px",
						overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
					},
				}, item.secondary)
				: null,
		);

		view.append(
			magGlass([thumb, body], {
				role: "button",
				onClick: () => navigate(item.route),
				style: { display: "flex", alignItems: "center", gap: "12px", padding: "10px 12px", marginBottom: "9px" },
			}),
		);
	}
}
